
import React, { useEffect, useRef, useState } from 'react';
import { Visibility, VisibilityOff } from '@mui/icons-material';
import { Dialog, DialogContent, IconButton, Button, Typography, Grid, Grid2, FormControlLabel, Checkbox, Avatar } from '@mui/material';
import ReCAPTCHA from "react-google-recaptcha";
import Loader from '../Loader';
import CaptchaWrapper from './CaptchaWrapper';
import { useApi } from '../../services/ApiServices';


interface RegisterComponentProps {
  onRegisterSuccess: (email: string) => void;
  onSwitchToLogin: () => void;
  onClose: () => void;
  rechaptchaSiteKey: string;
}

const RegisterComponent: React.FC<RegisterComponentProps> = ({ onRegisterSuccess, onSwitchToLogin, onClose, rechaptchaSiteKey }) => {
  const { ApiRequest } = useApi();
  const [email, setEmail] = useState('');
  const [celphone, setCelphone] = useState('');
  const [password, setPassword] = useState('');
  const [passwordConfirmation, setPasswordConfirmation] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showPasswordConfirm, setShowPasswordConfirm] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [captchaValue, setCaptchaValue] = useState<string | null>(null);
  const [passwordRegister, setPasswordRegister] = useState('');
  const [emailOrUser, setEmailOrUser] = useState('');
  const [errorMessage, setErrorMessage] = useState('');
  const [acceptedTerms, setAcceptedTerms] = useState(false);
  const recaptchaRef = useRef<any>(null);

  const handleCaptchaChange = (value: string | null) => {
    setCaptchaValue(value);
  };

  const handleTogglePasswordVisibility = () => {
    setShowPassword(prev => !prev);
  };
  const handleTogglePasswordConfirmationVisibility = () => {
    setShowPasswordConfirm(prev => !prev);
  };

  const handleRegister = async () => {
    if (!emailOrUser || !celphone || !passwordRegister || !passwordConfirmation) {
      alert('Por favor, completa todos los campos.');
      return;
    }
    if (passwordRegister !== passwordConfirmation) {
      alert('Las contraseñas deben coincidir.');
      return;
    }

    const passwordRegex = /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[!@#$%^&*()_+\[\]{}|;:,.?/~`\\\-]).{6,}$/;
    if (!passwordRegex.test(passwordRegister)) {
      alert('La contraseña debe tener al menos:\n- 6 caracteres\n- Una letra mayúscula\n- Una letra minúscula\n- Un número\n- Un carácter especial');
      return;
    }

    if (!captchaValue) {
      alert('Por favor, completa el reCAPTCHA.');
      return;
    }
    setIsLoading(true);
    try {
      const body = JSON.stringify({
        email: emailOrUser.toLocaleLowerCase(),
        name: emailOrUser.toLocaleLowerCase(),
        username: emailOrUser.toLocaleLowerCase(),
        phoneNumber: celphone,
        Password: passwordRegister,
        CaptchaToken: captchaValue
      });
      const response = await ApiRequest('api/Auth/Registro', 'POST', body, true);
      if (response.error) {
        alert(response.error);
      } else {
        onRegisterSuccess(emailOrUser);
      }
    } catch (error) {
      alert('Error en el registro. Inténtalo nuevamente.');
      console.error(error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleCelphoneChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const input = e.target.value;
    const phoneRegex = /^[0-9()+]*$/;
    if (phoneRegex.test(input) && input.length <= 20) {
      setCelphone(input);
    }
  };

  const validateEmail = (email: string) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value;
    setEmailOrUser(value);

    if (!validateEmail(value)) {
      setErrorMessage('Por favor, ingresa un correo electrónico válido.');
    } else {
      setErrorMessage('');
    }
  };

  return (
    <DialogContent className="login-modal-paper-register">
      <Grid2 className="login-modal-content">
        <Grid2 className="main-text">
          <span className="highlight-gray">Inicia tu registro</span>
        </Grid2>

        <Grid container direction="column" spacing={2} className="modal-container-title">
          <Typography variant="h6" className="login-title">
            Ingresa tu correo electrónico
          </Typography>
          <Grid2 className="login-search-box">
            <input 
              className="login-search-input" 
              type="email" 
              placeholder="Ejemplo: ejemplo@correo.com" 
              value={emailOrUser}
              onChange={handleInputChange}
            />
          </Grid2>
          <Grid2>
            {errorMessage && <p style={{ color: 'red' }}>{errorMessage}</p>}
          </Grid2>

          <Typography variant="h6" className="login-title">
            Ingresa tu número de celular
          </Typography>
          <Grid2 className="login-search-box">
            <input 
              className="login-search-input" 
              type="tel" 
              placeholder="Ejemplo: (52) 55 - 3456-9087" 
              value={celphone}
              onChange={handleCelphoneChange}
            />
          </Grid2>
          
          <Typography variant="h6" className="login-title">
            Contraseña
          </Typography>
          <Grid2 className="login-search-box">
            <Grid2 className="login-search-icon">
              <IconButton onClick={handleTogglePasswordVisibility} size="small">
                {!showPassword ? <VisibilityOff /> : <Visibility />}
              </IconButton>
            </Grid2>
            <input 
              className="login-search-input" 
              type={showPassword ? 'text' : 'password'}
              placeholder="Ingresa tu contraseña" 
              value={passwordRegister}
              onChange={(e) => setPasswordRegister(e.target.value)}
            />
          </Grid2>

          <Typography variant="h6" className="login-title">
            Confirmar contraseña
          </Typography>
          <Grid2 className="login-search-box">
            <Grid2 className="login-search-icon">
              <IconButton onClick={handleTogglePasswordConfirmationVisibility} size="small">
                {!showPasswordConfirm ? <VisibilityOff /> : <Visibility />}
              </IconButton>
            </Grid2>
            <input 
              className="login-search-input" 
              type={showPasswordConfirm ? 'text' : 'password'}
              placeholder="Confirma tu contraseña" 
              value={passwordConfirmation}
              onChange={(e) => setPasswordConfirmation(e.target.value)}
            />
          </Grid2>

          {/* Checkbox con términos y condiciones */}
          <Grid2 container justifyContent="center" alignItems="center" width="100%">
            <FormControlLabel
              control={
                <Checkbox 
                  checked={acceptedTerms} 
                  onChange={(e) => setAcceptedTerms(e.target.checked)} 
                />
              }
              label={
                <span className='terminosycondiciones'>
                  He leído y acepto los{' '}
                  <a 
                    href="/TermsConditions" 
                    style={{
                      color: '#666666',
                      textDecoration: 'underline',
                      textUnderlinePosition: 'from-font',
                      textDecorationSkipInk: 'none',
                    }}
                    target="_blank" 
                    rel="noopener noreferrer"
                  >
                    Términos y Condiciones
                  </a>
                </span>
              }
            />
          </Grid2>

          <Grid2 container justifyContent="center" alignItems="center" width="100%">
          <ReCAPTCHA
            sitekey={rechaptchaSiteKey} // reemplaza con tu clave del sitio
            onChange={handleCaptchaChange}
            ref={recaptchaRef}
          />
          </Grid2>

          {/* Botón de login */}
          <Grid2 container justifyContent="center" alignItems="center" width="100%">
            <Button 
              className="login-button-enviar" 
              variant="contained" 
              color="primary" 
              onClick={handleRegister}
              style={{ textTransform: 'none' }}
              disabled={
                !emailOrUser || 
                !celphone || 
                !passwordRegister || 
                !passwordConfirmation || 
                passwordRegister !== passwordConfirmation || 
                !acceptedTerms ||
                !captchaValue // recaptchaRef
              }
            >
              Continuar
            </Button>
          </Grid2>
        </Grid>
      </Grid2>
    </DialogContent>
  );
};

export default RegisterComponent;
